
import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for overflow minimum items updates.
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }

    if (name === 'minimum-items' && this.#refs) {
      this.#reflowItems();
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    await this.#waitForStyles();

    this.#initialize();

    window.addEventListener('resize', this.#handleViewportResize);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();

    window.removeEventListener('resize', this.#handleViewportResize);
  }

  #waitForStyles() {
    const styles = this.shadowRoot?.querySelector(
      'link[rel="stylesheet"]'
    );

    if (!styles || styles.sheet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      styles.addEventListener('load', resolve, { once: true });
      styles.addEventListener('error', resolve, { once: true });
    });
  }

  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) {
      throw new Error('Missing shadow root');
    }

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <overflow-list />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    this.addEventListener('reflow', (event) => {
      this.#reflowItems(0, event.detail?.lastVisibleElement ?? null);
    });

    const elements = defaultSlot
      .assignedElements()
      .filter((element) => !(element instanceof HTMLTemplateElement));

    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }

    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }

    this.#reflowItems();
  }

  #isMoreEnabled() {
    return window.innerWidth < 1280;
  }

  #handleViewportResize = () => {
    if (!this.#refs || this.#scheduled) return;

    this.#handleChange();
  };

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : (callback) => requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    return this.#refs.overflowSlot;
  }

  get defaultSlot() {
    return this.#refs.defaultSlot;
  }

  #handleIntersection = (entries) => {
    const entry = entries[0];

    if (entry?.isIntersecting) {
      this.#intersectionObserver.disconnect();

      setTimeout(() => {
        this.querySelector(
          ':scope > template[shadowrootmode="open"]'
        )?.remove();

        this.#reflowItems(entry.boundingClientRect.height);
      }, 0);
    }
  };

  #handleChange = () => {
    if (this.#scheduled) return;

    this.#scheduled = true;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.#refs) {
          this.#reflowItems();
        }

        this.#scheduled = false;
      }, 0);
    });
  };

  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  #reset() {
    const { list, placeholder, moreSlot } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    placeholder.style.removeProperty('width');
    placeholder.style.removeProperty('display');
    placeholder.hidden = true;
    moreSlot.hidden = true;

    this.style.setProperty('--overflow-count', '0');
  }

  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');

    this.setAttribute('disabled', 'true');
  }

  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    if (!this.#refs) return;

    const {
      defaultSlot,
      overflowSlot,
      moreSlot,
      list,
      placeholder,
    } = this.#refs;

    this.#unobserveChanges();

    // Restore all items before calculating their layout.
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot
      .assignedElements()
      .filter((element) => !(element instanceof HTMLTemplateElement));

    if (!elements.length) {
      moreSlot.hidden = true;
      placeholder.hidden = true;
      this.style.setProperty('--overflow-count', '0');
      this.#observeChanges();
      return;
    }

    // At 1280px and above, keep every item in the main navigation.
    if (!this.#isMoreEnabled()) {
      moreSlot.hidden = true;
      placeholder.hidden = true;
      placeholder.style.removeProperty('width');
      list.style.removeProperty('height');
      list.style.removeProperty('flex-wrap');
      list.style.removeProperty('overflow');
      list.style.removeProperty('counter-reset');

      this.style.setProperty('--overflow-count', '0');
      this.removeAttribute('minimum-reached');

      this.#observeChanges();
      return;
    }

    let visibleElements = [];
    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    } else {
      list.style.removeProperty('height');
    }

    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    lastVisibleElement?.style.setProperty('order', '-1');

    const moreSlotRect = moreSlot.getBoundingClientRect();

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreSlotRect.top) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        hasOverflow = true;
        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    });

    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }

    lastVisibleElement?.style.removeProperty('order');

    // Move overflowing elements into the overflow slot.
    for (const element of elements) {
      const targetSlot = overflowingElements.includes(element)
        ? overflowSlot.name
        : defaultSlot.name;

      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    list.style.setProperty(
      'counter-reset',
      `overflow-count ${overflowingElements.length}`
    );

    this.style.setProperty(
      '--overflow-count',
      `${overflowingElements.length}`
    );

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      placeholder.style.removeProperty('display');
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
      this.#updateMinimumReached(visibleElements);
    } else {
      placeholder.hidden = true;
      placeholder.style.removeProperty('width');
      this.removeAttribute('minimum-reached');
    }

    list.style.setProperty('overflow', 'unset');

    this.#observeChanges();
  };

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #refs;

  #resizeObserver = new ResizeNotifier(this.#handleChange);

  #mutationObserver = new MutationObserver(this.#handleChange);

  #intersectionObserver = new IntersectionObserver(
    this.#handleIntersection,
    {
      rootMargin: '640px 360px 640px 360px',
    }
  );
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
